import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Enrollment, Client, LoyaltyPlan } from '../types';
import './Pages.css';

interface RegisterRedemptionProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface RegisterRedemptionDto {
  clientId: string;
  transactionDate: string;
  notes?: string;
}

const RegisterRedemption: React.FC<RegisterRedemptionProps> = ({ onClose, onSuccess }) => {
  const [clients, setClients] = useState<Array<Enrollment & { client?: Client }>>([]);
  const [filteredClients, setFilteredClients] = useState<Array<Enrollment & { client?: Client }>>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activePlan, setActivePlan] = useState<LoyaltyPlan | null>(null);
  
  const [formData, setFormData] = useState<RegisterRedemptionDto>({
    clientId: '',
    transactionDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load active plan
      try {
        const dashboardRes = await api.get('/admin/dashboard');
        if (dashboardRes.data.activePlan) {
          setActivePlan(dashboardRes.data.activePlan);
        }
      } catch (error) {
        console.error('Error loading active plan:', error);
      }
      
      // Load clients
      const response = await api.get<Enrollment[]>('/admin/clients');
      
      // Para cada enrollment, buscar dados do cliente
      const clientsWithDetails = await Promise.all(
        response.data.map(async (enrollment) => {
          try {
            const clientResponse = await api.get<{ client: Client }>(`/admin/clients/${enrollment.clientId}/details`);
            return {
              ...enrollment,
              client: clientResponse.data.client
            };
          } catch (error) {
            console.error(`Error loading client ${enrollment.clientId}:`, error);
            return enrollment;
          }
        })
      );
      
      setClients(clientsWithDetails);
      setFilteredClients(clientsWithDetails);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientSearch.trim()) {
      setFilteredClients(clients);
      return;
    }

    const searchLower = clientSearch.toLowerCase().trim();
    const filtered = clients.filter((enrollment) => {
      const client = enrollment.client;
      if (!client) return false;

      const name = (client.name || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      const cpf = (client.cpf || '').toLowerCase();
      
      return name.includes(searchLower) || 
             phone.includes(searchLower) || 
             cpf.includes(searchLower);
    });

    setFilteredClients(filtered);
    setShowClientDropdown(true);
  }, [clientSearch, clients]);

  const handleClientSelect = (enrollment: Enrollment & { client?: Client }) => {
    setFormData({...formData, clientId: enrollment.clientId});
    setClientSearch(enrollment.client?.name || enrollment.clientId);
    setShowClientDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.clientId) {
      alert('Por favor, selecione um cliente');
      return;
    }

    try {
      setLoading(true);

      const payload: RegisterRedemptionDto = {
        clientId: formData.clientId,
        transactionDate: formData.transactionDate,
        notes: formData.notes || undefined
      };

      await api.post('/admin/transactions/redemption', payload);
      
      alert('Resgate registrado com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error registering redemption:', error);
      const message = error.response?.data?.message || 'Erro ao registrar resgate';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.clientId === formData.clientId);
  const selectedClientPlan = activePlan;

  useEffect(() => {
    if (selectedClient) {
      setClientSearch(selectedClient.client?.name || selectedClient.clientId);
    }
  }, [selectedClient]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.form-group')) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showClientDropdown]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
          <h2>Registrar Resgate</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {selectedClientPlan && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px', 
            backgroundColor: '#f0f9ff', 
            borderRadius: '12px',
            border: '2px solid #3b82f6'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e40af' }}>
              Informações do Resgate
            </div>
            <div style={{ fontSize: '14px', color: '#1e3a8a' }}>
              <div><strong>Pontos necessários:</strong> {selectedClientPlan.targetPoints} pts</div>
              <div style={{ marginTop: '4px' }}>
                <strong>Prêmio:</strong> {selectedClientPlan.rewardValue 
                  ? `R$ ${selectedClientPlan.rewardValue.toFixed(2)}`
                  : selectedClientPlan.rewardDescription}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>Cliente *</label>
            <input
              type="text"
              required
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                if (!e.target.value) {
                  setFormData({...formData, clientId: ''});
                }
              }}
              onFocus={() => setShowClientDropdown(true)}
              placeholder="Buscar por nome, telefone ou CPF..."
              style={{ width: '100%', padding: '14px 16px', border: '2px solid var(--border)', borderRadius: '12px', fontSize: '15px' }}
            />
            {showClientDropdown && filteredClients.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid var(--border)',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}>
                {filteredClients.map((enrollment) => {
                  const canRedeem = enrollment.currentPoints >= (selectedClientPlan?.targetPoints || 0);
                  return (
                    <div
                      key={enrollment.enrollmentId}
                      onClick={() => handleClientSelect(enrollment)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--bg-gray)',
                        transition: 'background-color 0.2s',
                        opacity: canRedeem ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-gray)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                        {enrollment.client?.name || enrollment.clientId}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px' }}>
                        {enrollment.client?.phone && `📱 ${enrollment.client.phone}`}
                        {enrollment.client?.cpf && ` • CPF: ${enrollment.client.cpf}`}
                        {` • ${enrollment.currentPoints} pts`}
                        {!canRedeem && (
                          <span style={{ color: '#DC2626', marginLeft: '8px' }}>
                            (Pontos insuficientes)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedClient && selectedClientPlan && (
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '8px', fontSize: '14px' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Cliente selecionado: {selectedClient.client?.name || selectedClient.clientId}</div>
                <div><strong>Pontos atuais:</strong> {selectedClient.currentPoints} pts</div>
                <div><strong>Pontos necessários:</strong> {selectedClientPlan.targetPoints} pts</div>
                {selectedClient.currentPoints < selectedClientPlan.targetPoints && (
                  <div style={{ color: '#DC2626', marginTop: '4px', fontWeight: 600 }}>
                    ⚠️ Cliente não possui pontos suficientes para resgate
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Data do Resgate *</label>
            <input
              type="date"
              required
              value={formData.transactionDate}
              onChange={(e) => setFormData({...formData, transactionDate: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Observações (Opcional)</label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Observações sobre o resgate..."
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !!(selectedClient && selectedClientPlan && selectedClient.currentPoints < selectedClientPlan.targetPoints)}
            >
              {loading ? 'Processando...' : '✓ Registrar Resgate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterRedemption;

